import { useState } from 'react';

import { AGGREGATE_NAMES, LOAN_REQUEST_COMMANDS } from 'cpool-constants';
import { useTranslation } from 'react-i18next';
import { LuDownload, LuTrash2 } from 'react-icons/lu';

import { DeleteDocumentModal } from '../DeleteDocumentModal';
import { useRefreshRecordedDocuments } from '../useRefreshRecordedDocuments';
import { APICommunicator } from '~/api/APICommunicator';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import useCallbackWithErrorHandler from '~/hooks/useCallbackWithErrorHandler';
import { downloadFile } from '~/utils/downloadFile';

type Props = {
    documentKey: string;
    documentName: string;
    leadId: string;
    mimeType: string;
};

export const RowActions: React.FC<Props> = ({ documentKey, documentName, leadId, mimeType }) => {
    const { t } = useTranslation();
    const refreshRecordedDocuments = useRefreshRecordedDocuments(leadId);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const { permissions } = useAclPermissions({ loanRequestId: leadId });
    const isDeleteDisabled = !permissions.commands[AGGREGATE_NAMES.LOAN_REQUEST].removeDocument;

    const download = useCallbackWithErrorHandler(
        async () =>
            downloadFile({
                mimeType,
                name: documentName,
                route: `/documents/download?aggregateId=${leadId}&key=${documentKey}`,
            }),
        [documentKey, documentName, leadId, mimeType],
    );

    const remove = useCallbackWithErrorHandler(async () => {
        setShowDeleteModal(false);

        const { position } = await APICommunicator.sendCommand({
            aggregateId: leadId,
            aggregateName: AGGREGATE_NAMES.LOAN_REQUEST,
            commandName: LOAN_REQUEST_COMMANDS.removeDocument,
            payload: { key: documentKey },
        });
        await refreshRecordedDocuments(position);
    }, [documentKey, leadId, refreshRecordedDocuments]);

    return (
        <div className="flex items-center justify-center gap-2">
            <button
                aria-label={t('DOCUMENT_DEMANDER.SUMMARY.DOWNLOAD_FILE')}
                className="text-midnight"
                data-testid={`file-download-button-${documentKey}`}
                onClick={download}
                type="button"
            >
                <LuDownload size={16} />
            </button>
            <button
                aria-label={t('DOCUMENT_DEMANDER.SUMMARY.DELETE_FILE')}
                className="text-midnight disabled:cursor-not-allowed disabled:opacity-50"
                data-testid={`file-delete-button-${documentKey}`}
                disabled={isDeleteDisabled}
                onClick={() => setShowDeleteModal(true)}
                type="button"
            >
                <LuTrash2 size={16} />
            </button>
            <DeleteDocumentModal open={showDeleteModal} onClose={() => setShowDeleteModal(false)} onConfirm={remove} />
        </div>
    );
};
